/**
 * Object operations every editing host shares: create, duplicate, delete,
 * reparent and show or hide. Each asks everything that can refuse before it
 * changes anything, and returns one undo step for what it did (see
 * `types.ts`).
 *
 * An operation on several objects acts on the top-most of them: a descendant
 * of another target goes with its ancestor.
 */

import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';

import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import type { Vec3 } from '../../scenedata/common';
import { ImageAnchor } from '../../behaviors/imageanchor';
import type { UndoCommand } from '../undo/UndoCommand';
import { ActiveStateCommand } from '../undo/ActiveStateCommand';
import { CreateObjectCommand } from '../undo/CreateObjectCommand';
import { DeleteObjectCommand } from '../undo/DeleteObjectCommand';
import { ReparentCommand } from '../undo/ReparentCommand';
import { ownNodes } from '../undo/PlacedCopyCommand';
import { readMarks, restoreReferences } from '../undo/referenceMarks';
import { pruneEmptyRig } from '../states/rigContainer';
import { createFromTemplate, restOnGround, type CreateFromTemplateOptions } from '../create/createObject';
import { OBJECT_TEMPLATES, type ObjectTemplate, type TemplateDescriptor } from '../create/templates';
import { structuralRootRefusal, topLevelParent } from './documentRoot';
import { copyForInsideEdit } from './referenceEdit';
import { bundle, capture, descendantEntries, rollBack } from './steps';
import { editRefusal, keyframeRefusal, prepareBatch, refuse, sceneOf } from './checks';
import { pageStateRefusal, routeOf, type Route } from './editRoute';
import { capturePageState } from '../states/pageCapture';
import { editTargetState, stateActiveCommand } from '../states/statesActions';
import type { AuthoringPorts, BatchResult, OpResult, Refusal } from './types';

/** A local pose to give an object. `rotation` is Euler degrees. */
export interface ObjectPose {
    position?: Vec3;
    rotation?: Vec3;
    scale?: Vec3;
}

/**
 * A primitive's size, in metres, when it is made with no `scale`: the base
 * mesh is 1 m, made at half. It is Studio's Add-menu size, chosen as the
 * room-scale sweet spot (Greg, 2026-07-19): present at phone-AR distance,
 * neither furniture (1 m) nor a bauble (0.25 m). The AI tools make
 * primitives at it too (Martin, #1234, 2026-10-15).
 */
export const PRIMITIVE_SPAWN_SCALE = 0.5;

export interface CreateObjectArgs extends ObjectPose {
    /** Default: the top of the scene (`topLevelParent`). */
    parent?: SceneObject | null;
    /** Default: the template's. A name a sibling has gets a suffix. */
    name?: string;
    behaviorData?: CreateFromTemplateOptions['behaviorData'];
    /** Default true. False creates it hidden, in one undo step. */
    active?: boolean;
}

/**
 * Create an object from a template and give it the pose in `args`. A
 * primitive given no `scale` is made {@link PRIMITIVE_SPAWN_SCALE} across. A
 * primitive given no `position` then rests on world y = 0, as the editor drops
 * one, at its posed size and under whatever frame its parent has. Refuses
 * play mode, a deleted parent, and a parent inside a scene reference.
 */
export async function createObject(
    scene: TraceScene,
    template: ObjectTemplate | TemplateDescriptor,
    args: CreateObjectArgs = {},
): Promise<OpResult<{ object: SceneObject }>> {
    const parent = args.parent ?? topLevelParent(scene);
    if (parent) sceneOf([parent], scene);
    const refusal = editRefusal(scene, parent ? [parent] : [])
        ?? (parent ? parentRefusal(scene, args.name ?? templateName(template), parent) : null);
    if (refusal) return { refused: refusal };

    // A template that fails takes back what it made.
    const object = await createFromTemplate(scene, template, { parent, name: args.name, behaviorData: args.behaviorData, groundAlign: false });
    try {
        const descriptor = typeof template === 'string' ? OBJECT_TEMPLATES[template] : template;
        const scale: Vec3 = [PRIMITIVE_SPAWN_SCALE, PRIMITIVE_SPAWN_SCALE, PRIMITIVE_SPAWN_SCALE];
        applyPose(object, descriptor.primitive && !args.scale ? { ...args, scale } : args);
        if (descriptor.primitive && !args.position) restOnGround(object);
        if (args.active === false) object.setActive(false);
        // Asked again: the scene may have changed while the template loaded.
        const late = editRefusal(scene, parent ? [parent] : []);
        if (late) {
            discard(scene, [object]);
            return { refused: late };
        }
        return { object, command: new CreateObjectCommand(object, parent, capture(object), descendantEntries(object)) };
    } catch (error) {
        discard(scene, [object]);
        throw error;
    }
}

/**
 * Copy each object, with everything under it, beside the original. The copy's
 * root takes `args.name` (default: the original's), with a suffix when a
 * sibling has it, and the pose in `args`; the nodes under it keep theirs.
 * A library reference is copied as a reference: the copy loads the same
 * document, and what the document derives is not copied as the scene's own.
 * `copies` maps each original to its copy.
 *
 * Refuses play mode, a deleted object, a structural root (a copy of the
 * document root would be a second root), an object that is or holds the
 * carrier of the scene's `ImageAnchor` (a scene has one marker), and an object
 * inside a scene reference.
 */
export async function duplicateObjects(
    objects: SceneObject | readonly SceneObject[],
    args: ObjectPose & { name?: string } = {},
): Promise<OpResult<{ copies: Map<SceneObject, SceneObject> }>> {
    const sources = topMost(objects);
    const scene = sceneOf(sources);
    const refusal = editRefusal(scene, sources)
        ?? firstRefusal(sources, source => rootRefusal(source, 'duplicate'))
        ?? firstRefusal(sources, source => anchorRefusal(source, 'duplicated'))
        ?? firstRefusal(sources, source => parentRefusal(scene, source.name, source.getParent()));
    if (refusal) return { refused: refusal };

    const copies = new Map<SceneObject, SceneObject>();
    const commands: UndoCommand[] = [];
    const made: SceneObject[] = [];
    try {
        for (const source of sources) {
            const parent = source.getParent();
            // What a placed reference derives is not the scene's: it comes back
            // when the copy's reference loads again, never as data.
            const subtree = ownNodes(source);
            const marks = subtree.map(readMarks);
            const data = subtree.map(capture);
            data[0] = { ...data[0], name: args.name ?? source.name };
            // Keyed by the original, not by name: names are unique only among
            // siblings, so two nodes in one subtree can share one.
            const copyOf = new Map<SceneObject, SceneObject>();
            for (const [i, node] of subtree.entries()) {
                const copyParent = i === 0 ? parent : copyOf.get(node.getParent()!)!;
                const copy = await scene.createSceneObjectFromData(data[i], copyParent ?? undefined);
                made.push(copy);
                copyOf.set(node, copy);
            }
            const root = copyOf.get(source)!;
            // Before the command below reads the copy: a rebuilt reference is an
            // ordinary node until it is marked and loaded again.
            await restoreReferences(scene, subtree.map((node, i) => ({ object: copyOf.get(node)!, marks: marks[i] })), 'duplicateObjects', root.name);
            applyPose(root, args);
            copies.set(source, root);
            commands.push(new CreateObjectCommand(root, parent ?? topLevelParent(scene), capture(root), descendantEntries(root)));
        }
    } catch (error) {
        discard(scene, made);
        throw error;
    }
    // Asked again: the scene may have changed while the copies loaded.
    const late = editRefusal(scene, sources);
    if (late) {
        discard(scene, made);
        return { refused: late };
    }
    return { copies, command: bundle(`Duplicate ${sources.length} objects`, commands) };
}

/**
 * Delete each object with everything under it. A rig the delete leaves empty
 * is deleted in the same step (`pruneEmptyRig`). An object inside a placed
 * reference copies the reference into the scene first (`copyForInsideEdit`),
 * also in the same step; `orphaned` lists the files that copy could not bring
 * into the folder, which a save cannot carry. Inside a reference the copy rule
 * leaves alone (anything but a library reference, or any reference in a legacy
 * session), the delete is made in place, and its undo puts the node back
 * there. The copy rule does not cover that case; a host that refuses it holds
 * such objects read-only (`ports.readOnly`).
 *
 * Refuses play mode, a deleted object, a structural root, an object that is or
 * holds the carrier of the scene's `ImageAnchor`, an object the host holds
 * read-only, and a copy the reference rule refuses.
 */
export async function deleteObjects(
    objects: SceneObject | readonly SceneObject[],
    ports: AuthoringPorts = {},
): Promise<OpResult<{ deleted: SceneObject[]; orphaned: string[] }>> {
    const targets = topMost(objects);
    const scene = sceneOf(targets);
    const refusal = editRefusal(scene, targets)
        ?? firstRefusal(targets, target => rootRefusal(target, 'delete'))
        ?? firstRefusal(targets, target => anchorRefusal(target, 'deleted'));
    if (refusal) return { refused: refusal };

    const copy = await copyForInsideEdit(targets, ports);
    if ('refused' in copy) return copy;
    const steps: UndoCommand[] = copy.command ? [copy.command] : [];
    // Asked again: the scene may have changed while the copy uploaded.
    const late = editRefusal(scene, targets);
    if (late) {
        await rollBack(steps, late);
        return { refused: late };
    }
    try {
        for (const target of targets) {
            const parent = target.getParent();
            // Recorded while the subtree stands: the command reads each node's parent.
            const command = new DeleteObjectCommand(
                target,
                [target, ...target.getDescendants()].map(object => ({ object, data: capture(object), parent: object.getParent() })),
                parent,
            );
            scene.deleteSceneObject(target);
            steps.push(command);
            // After the delete it follows, so its undo runs first and the
            // object comes back under the live rig.
            const prune = pruneEmptyRig(parent);
            if (prune) steps.push(prune);
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const label = targets.length === 1 ? `Delete ${targets[0].name}` : `Delete ${targets.length} objects`;
    return { deleted: targets, orphaned: copy.orphaned, command: bundle(label, steps) };
}

/**
 * Move each object under `parent` (null: the top of the scene), keeping its
 * world pose, as the last child. An object whose name a new sibling has gets
 * a suffix. An object inside a placed reference copies the reference into the
 * scene first, in the same step (`orphaned`, and a reference the copy rule
 * leaves alone, where the move is made in place: as for {@link deleteObjects}).
 *
 * Refuses play mode, a deleted object or parent, a structural root, an object
 * that is or holds the scene's `ImageAnchor` carrier, a parent that is the
 * object or under it, a parent inside a scene reference, an object the host
 * holds read-only, a reparent policy's veto, and a copy the reference rule
 * refuses.
 */
export async function setParent(
    objects: SceneObject | readonly SceneObject[],
    parent: SceneObject | null,
    ports: AuthoringPorts = {},
): Promise<OpResult<{ moved: SceneObject[]; orphaned: string[] }>> {
    const moving = topMost(objects);
    const scene = sceneOf(moving);
    const destination = parent ?? topLevelParent(scene);
    if (!destination) throw new Error('[setParent] the scene has no root to move objects to');
    sceneOf([destination], scene);
    const refusal = editRefusal(scene, [...moving, destination])
        ?? firstRefusal(moving, object => rootRefusal(object, 'move'))
        ?? firstRefusal(moving, object => anchorRefusal(object, 'reparented'))
        ?? firstRefusal(moving, object => cycleRefusal(object, destination))
        ?? firstRefusal(moving, object => parentRefusal(scene, object.name, destination));
    if (refusal) return { refused: refusal };

    const copy = await copyForInsideEdit(moving, ports);
    if ('refused' in copy) return copy;
    const steps: UndoCommand[] = copy.command ? [copy.command] : [];

    // Asked again, as the scene may have changed while the copy uploaded. The
    // host's policy is asked only now: a policy that sees an object still
    // inside a reference may start a copy of its own.
    const vetoed = moving.find(object => object.getParent() !== destination && !scene._isReparentAllowed(object, destination));
    const late = editRefusal(scene, [...moving, destination]) ?? (vetoed ? refuse(
        'REPARENT_REFUSED',
        `Reparenting "${vetoed.name}" under "${destination.name}" was vetoed by the scene's reparent policy`,
        vetoed,
    ) : null);
    if (late) {
        await rollBack(steps, late);
        return { refused: late };
    }
    try {
        for (const object of moving) steps.push(ReparentCommand.perform(object, destination));
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const label = moving.length === 1 ? `Reparent ${moving[0].name}` : `Move ${moving.length} objects`;
    return { moved: moving, orphaned: copy.orphaned, command: bundle(label, steps) };
}

/**
 * Show or hide each object, as one undo step for the entries that change what
 * it shows. The edit is written where it is seen (`editRoute.ts`): into the
 * showing page's state for a Pages container's shared content while
 * `ports.pageEditing` says page editing is on for it, else into the state that
 * shows on the object or its group (`editTargetState`), else into Base, the
 * object's own `active`.
 *
 * An object inside a placed reference copies the reference into the scene
 * first, in the same step, as {@link deleteObjects} does; `orphaned` lists the
 * files that copy could not bring across. A deleted object, or one whose copy
 * cannot be made, is refused on its own. An entry that changes what its
 * object shows is refused on its own while a keyframe is selected on it or an
 * ancestor (`STATE_ROUTED`: the edit would land on Base, out of sight), and
 * when it shows the state its page assigns it or an ancestor while
 * `ports.pageEditing` is off (`STATE_ROUTED`: that state is the page's, maybe
 * other pages' too). While the scene plays or an undo runs, every entry is.
 */
export async function setActive(
    entries: ReadonlyArray<{ object: SceneObject; active: boolean }>,
    ports: AuthoringPorts = {},
): Promise<BatchResult<SceneObject> & { orphaned: string[] }> {
    if (entries.length === 0) return { applied: [], refused: [], command: null, orphaned: [] };
    const changes = ({ object, active }: { object: SceneObject; active: boolean }) => object.active !== active;
    const routed = ({ object, active }: { object: SceneObject; active: boolean }): Refusal | null =>
        changes({ object, active })
            ? keyframeRefusal(object, 'visibility') ?? pageStateRefusal(object, ports, editTargetState, 'visibility')
            : null;
    const { ready, refused, steps, orphaned } = await prepareBatch(entries, ports, { check: routed, late: routed, changes });

    let changed = 0;
    try {
        for (const { entry: { object, active } } of ready) {
            if (object.active === active) continue;
            steps.push(await recordActive(object, active, routeOf(object, ports, editTargetState)));
            changed++;
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const label = changed > 0 ? `Show or hide ${changed} objects` : 'Copy references into the scene';
    const command = steps.length > 0 ? bundle(label, steps) : null;
    return { applied: ready.map(({ entry }) => entry.object), refused, command, orphaned };
}

/** Write `active` where `route` says it is seen, and return the command for it. */
async function recordActive(object: SceneObject, active: boolean, route: Route): Promise<UndoCommand> {
    if (route.to === 'page') {
        const verb = active ? 'Show' : 'Hide';
        return capturePageState(route.page, `${verb} ${object.name} on ${route.page.pageName}`, state => ({ ...state, active }));
    }
    if (route.to === 'state') {
        const command = await stateActiveCommand(object, active);
        if (!command) throw new Error(`[setActive] the state "${route.state}" of "${object.name}" stored nothing for the edit`);
        return command;
    }
    // The authored flag, not what a state shows: undo writes this back.
    const before = object.authoredActive;
    object.setActive(active);
    return new ActiveStateCommand(object, before, active);
}

// ── Checks ───────────────────────────────────────────────────────────────────

function rootRefusal(object: SceneObject, verb: 'delete' | 'move' | 'duplicate'): Refusal | null {
    const message = structuralRootRefusal(object, verb);
    return message ? refuse('STRUCTURAL_ROOT', message, object) : null;
}

/** Why `object` may not be deleted, copied or moved: it, or a node under it, carries the scene's `ImageAnchor`. */
function anchorRefusal(object: SceneObject, past: 'deleted' | 'duplicated' | 'reparented'): Refusal | null {
    const carrier = [object, ...object.getDescendants()].find(node => node.getBehavior(ImageAnchor));
    if (!carrier) return null;
    return refuse(
        'SCENE_ANCHOR',
        `"${carrier.name}" is this scene's marker — a location-based scene needs it, so it cannot be ${past}`
            + ' (change or remove the marker from the Scene panel instead)',
        carrier,
    );
}

function cycleRefusal(object: SceneObject, parent: SceneObject): Refusal | null {
    if (parent === object) return refuse('CYCLE', 'Cannot parent an object to itself', object);
    if (!object.getDescendants().includes(parent)) return null;
    return refuse('CYCLE', `Cannot parent "${object.name}" to its own descendant "${parent.name}"`, object);
}

/**
 * Why nothing may be added under `parent`: it is, or is inside, a scene
 * reference. The scene's own veto, which also tells listeners.
 */
function parentRefusal(scene: TraceScene, name: string, parent: SceneObject | null): Refusal | null {
    if (!parent || scene.canParentUnder(parent)) return null;
    return refuse('REPARENT_REFUSED', scene._vetoReferenceSubtreeParent(name, parent).message, parent);
}

function firstRefusal(objects: readonly SceneObject[], check: (object: SceneObject) => Refusal | null): Refusal | null {
    for (const object of objects) {
        const refusal = check(object);
        if (refusal) return refusal;
    }
    return null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** The objects none of whose ancestors is also listed, in the order given, each once. */
function topMost(objects: SceneObject | readonly SceneObject[]): SceneObject[] {
    const list = [...new Set(Array.isArray(objects) ? objects as readonly SceneObject[] : [objects as SceneObject])];
    if (list.length === 0) throw new Error('[authoring] an operation needs at least one object');
    const listed = new Set(list);
    return list.filter(object => {
        for (let node = object.getParent(); node; node = node.getParent()) {
            if (listed.has(node)) return false;
        }
        return true;
    });
}

/** Delete what an operation made before it stopped, newest first. */
function discard(scene: TraceScene, made: readonly SceneObject[]): void {
    for (const object of [...made].reverse()) {
        if (!object.transform.isDisposed()) scene.deleteSceneObject(object);
    }
}

/** The name a template gives a new object: its default name ("Cube"), not its id ("cube"). */
function templateName(template: ObjectTemplate | TemplateDescriptor): string {
    return typeof template === 'string' ? OBJECT_TEMPLATES[template]?.defaultName ?? template : template.defaultName;
}

/** Give `object` the parts of the local pose `pose` names. */
function applyPose(object: SceneObject, pose: ObjectPose): void {
    const { position, rotation, scale } = pose;
    if (!position && !rotation && !scale) return;
    const transform = object.transform;
    if (position) transform.position = new Vector3(...position);
    if (rotation) {
        const toRadians = Math.PI / 180;
        transform.rotationQuaternion = Quaternion.FromEulerAngles(
            rotation[0] * toRadians, rotation[1] * toRadians, rotation[2] * toRadians,
        );
    }
    if (scale) transform.scaling = new Vector3(...scale);
    transform.computeWorldMatrix(true);
}
